import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DigisubservicesService } from '../services/digisubservices.service';
import * as XLSX from 'xlsx';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

const ADVT_LEDGER_NAME = 'जाहिरातदार / देणगीदार खाते';
const ISSUE_LEDGER_NAME = 'अंक विक्री / प्रतिनिधि मुख्य खाते';
const BOOK_LEDGER_NAME = 'पुस्तक प्रलंबित देय रक्कम';
// System inception - get_opening_balance_report.php sums vouchers from here.
const GST_INCEPTION_DATE = '2024-04-01';
// Balances below ₹1 are treated as 0 (rounding differences, not balances).
const MIN_BALANCE = 1;

// One row exactly as get_opening_balance_report.php returns it.
interface OpeningBalanceRow {
  exp_id: number;
  exp_name: string;
  sub_exp_id: number | null;
  sub_exp_name: string;
  exp_type: string;
  master_opening_bal: number | null;
  master_opening_type: string | null;
  total_cr: number | null;
  total_dr: number | null;
  opening_balance: number;
  // Expense_master.sort_order / Sub_Expense_Master.sort_order; null when
  // not set (or for rows this component adds itself).
  exp_sort_order?: number | null;
  sub_sort_order?: number | null;
}

// Missing sort_order sorts last.
function orderOf(v: number | null | undefined): number {
  return (v === null || v === undefined) ? Number.MAX_SAFE_INTEGER : v;
}

interface SubBalance {
  sub_exp_name: string;
  opening_bal: number;
  opening_bal_type: 'DR' | 'CR';
}

type Section = 'ASSETS' | 'LIABILITIES';

interface ExpenseGroup {
  exp_name: string;
  exp_type: string;
  section: Section;
  opening_bal: number;
  opening_bal_type: 'DR' | 'CR';
  sub_expenses: SubBalance[];
}

interface SectionView {
  key: Section;
  label: string;
  groups: ExpenseGroup[];
  totalDR: number;
  totalCR: number;
}

const SECTION_LABELS: Record<Section, string> = {
  ASSETS: 'मालमत्ता (Assets)',
  LIABILITIES: 'देणी (Liabilities)'
};

// API sends exp_type in mixed case ('ASSENT', 'Assent', 'LIABILITIES', ...);
// anything that isn't a liability is shown under Assets.
function toSection(expType: string | null | undefined): Section {
  return (expType || '').toUpperCase().startsWith('LIAB') ? 'LIABILITIES' : 'ASSETS';
}

@Component({
  selector: 'app-expense-opening-balance-report',
  templateUrl: './expense-opening-balance-report.component.html',
  styleUrls: ['./expense-opening-balance-report.component.scss']
})
export class ExpenseOpeningBalanceReportComponent implements OnInit {
  startDate: any;
  endDate: any;
  currentDate = new Date();
  rows: OpeningBalanceRow[] = [];
  groupedData: ExpenseGroup[] = [];
  isLoading = false;
  showDetails = false;
  searchTerm = '';

  constructor(
    private datePipe: DatePipe,
    private dialogRef: MatDialogRef<ExpenseOpeningBalanceReportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: DigisubservicesService
  ) { }

  ngOnInit(): void {
    this.startDate = this.data['startDate'];
    this.endDate = this.data['endDate'];
    this.loadData();
  }

  loadData() {
    this.isLoading = true;
    // Day before the report's start date, e.g. 2025-04-01 -> 2025-03-31.
    const prevDay = new Date(this.startDate);
    prevDay.setDate(prevDay.getDate() - 1);
    const gstRange = { end: this.datePipe.transform(prevDay, 'yyyy-MM-dd') || '' };
    forkJoin({
      opening: this.service.getOpeningBalanceReport({ request: { startdate: this.startDate } }),
      // Advt Personal Ledger (In Briefly) - its opening_balance_amount per
      // advertiser feeds the जाहिरातदार / देणगीदार खाते opening balance.
      advt: this.service.getPersoanlLedBriefly({
        start_date: this.startDate,
        end_date: this.endDate || this.startDate,
        advt_name: ''
      }).pipe(catchError(err => {
        console.error('Error in getPersoanlLedBriefly', err);
        return of({ data: [] });
      })),
      // Issue Seller Personal Ledger (Briefly) - its opening_bal per seller
      // feeds the अंक विक्री / प्रतिनिधि मुख्य खाते opening balance.
      issue: this.service.getIssuePersoanlLedBriefly({
        startDate: this.startDate,
        endDate: this.endDate || this.startDate,
        issue_seller_name: ''
      }).pipe(catchError(err => {
        console.error('Error in getIssuePersoanlLedBriefly', err);
        return of([]);
      })),
      // Book Seller Ledger (Briefly) - its opening_bal per seller feeds the
      // पुस्तक प्रलंबित देय रक्कम opening balance.
      book: this.service.getBookSellerLedgerBriefly({
        start_date: this.startDate,
        end_date: this.endDate || this.startDate,
        book_seller_name: ''
      }).pipe(catchError(err => {
        console.error('Error in getBookSellerLedgerBriefly', err);
        return of([]);
      })),
      // Advt Invoice Register (Briefly) - its CGST / SGST / IGST column
      // totals go to the credit side of the matching GST - जाहिरात ledgers.
      // Uses the back-year range (inception to the day before startDate),
      // the same range the opening API sums the GST vouchers (DR) over.
      advtInvoice: (gstRange.end >= GST_INCEPTION_DATE
        ? this.service.getAdvtInvoiceRegisterListbriefly({
            start_date: GST_INCEPTION_DATE,
            end_date: gstRange.end,
            issue_name: '',
            issue_date: ''
          })
        : of([])
      ).pipe(catchError(err => {
        console.error('Error in getAdvtInvoiceRegisterListbriefly', err);
        return of([]);
      }))
    })
      .subscribe({
        next: ({ opening, advt, issue, book, advtInvoice }: any) => {
          this.isLoading = false;
          this.rows = (opening && opening.status && Array.isArray(opening.data)) ? opening.data : [];
          const advtRows = this.buildLedgerOpeningRows(
            advt?.data, ADVT_LEDGER_NAME, -1, 'opening_balance_amount', 'advt_name');
          const issueRows = this.buildLedgerOpeningRows(
            issue, ISSUE_LEDGER_NAME, -2, 'opening_bal', 'issue_seller_name');
          const bookRows = this.buildLedgerOpeningRows(
            book, BOOK_LEDGER_NAME, -3, 'opening_bal', 'book_seller_name');
          const gstRows = [
            this.buildColumnTotalRow(advtInvoice, 'CGST - जाहिरात', -4, 'cgst'),
            this.buildColumnTotalRow(advtInvoice, 'SGST - जाहिरात', -5, 'sgst'),
            this.buildColumnTotalRow(advtInvoice, 'IGST - जाहिरात', -6, 'igst')
          ].filter((r): r is OpeningBalanceRow => r !== null);
          console.log('Opening report - advt invoice GST totals', {
            invoiceFrom: GST_INCEPTION_DATE, invoiceTo: gstRange.end,
            invoices: Array.isArray(advtInvoice) ? advtInvoice.length : 0,
            gst: gstRows.map(r => ({ ledger: r.exp_name, credit: r.opening_balance }))
          });
          this.rows = this.rows.concat(advtRows, issueRows, bookRows, gstRows);
          this.buildGroups();
        },
        error: () => {
          this.isLoading = false;
          this.rows = [];
          this.groupedData = [];
        }
      });
  }

  // Turns each party's opening balance from a personal-ledger briefly report
  // into a sub row under the given ledger name, so the group total equals the
  // Opening Balance column total of that report. A positive amount is
  // receivable (DR), and this report treats negative as DR, hence the flip.
  // fallbackExpId must be unique per ledger so buildGroups keeps them apart.
  private buildLedgerOpeningRows(list: any, ledgerName: string, fallbackExpId: number,
                                 amountField: string, nameField: string): OpeningBalanceRow[] {
    if (!Array.isArray(list)) { return []; }
    // Reuse the ledger's exp_id if the opening API already returned it, so
    // both land in one group instead of showing the account twice.
    const existing = this.rows.find(r => r.exp_name === ledgerName);
    const expId = existing ? existing.exp_id : fallbackExpId;
    const expType = existing ? existing.exp_type : 'Assent';

    return list
      .map((item, i) => ({ item, i, amt: parseFloat(item[amountField]) || 0 }))
      .filter(x => x.amt !== 0)
      .map(x => ({
        exp_id: expId,
        exp_name: ledgerName,
        sub_exp_id: x.i + 1,
        sub_exp_name: x.item[nameField] || '',
        exp_type: expType,
        master_opening_bal: null,
        master_opening_type: null,
        total_cr: null,
        total_dr: null,
        opening_balance: -x.amt,
        exp_sort_order: existing ? existing.exp_sort_order : null,
        sub_sort_order: null
      }));
  }

  // Sums one column of a briefly report into a single CR row (positive in
  // this report's convention) for the given ledger. It reuses the ledger's
  // exp_id when the opening API already returned it, so buildGroups nets
  // the two: the API's DR (GST paid) against this CR (GST charged on
  // invoices) - equal amounts come out as 0 and the line is hidden.
  private buildColumnTotalRow(list: any, ledgerName: string, fallbackExpId: number,
                              amountField: string): OpeningBalanceRow | null {
    if (!Array.isArray(list)) { return null; }
    const total = list.reduce((s, item) => s + (parseFloat(item[amountField]) || 0), 0);
    if (total === 0) { return null; }
    const existing = this.rows.find(r => r.exp_name === ledgerName);
    return {
      exp_id: existing ? existing.exp_id : fallbackExpId,
      exp_name: ledgerName,
      sub_exp_id: null,
      sub_exp_name: '',
      exp_type: existing ? existing.exp_type : 'Liabilities',
      master_opening_bal: null,
      master_opening_type: null,
      total_cr: null,
      total_dr: null,
      opening_balance: total,
      exp_sort_order: existing ? existing.exp_sort_order : null,
      sub_sort_order: null
    };
  }

  // Groups the flat API rows by exp_id purely for display: a main-only row
  // (sub_exp_id null, e.g. from the "no sub" query) becomes its own group
  // with no children; rows sharing an exp_id (sub-expenses, or bank
  // sub-accounts under 28) are summed into a single main total with each
  // one listed underneath. This is arithmetic on values already fetched,
  // not a re-derivation of the underlying vouchers.
  // Keyed by exp_id + section so a ledger whose sub-accounts sit on both
  // sides shows under each section with only its own sub-accounts. Banks
  // are keyed per sub-account instead, so each bank is its own line.
  private buildGroups() {
    const map = new Map<string, {
      exp_name: string;
      exp_type: string;
      section: Section;
      total: number;
      isBank: boolean;
      expOrder: number;
      subOrder: number;
      subs: { sub_exp_name: string; amount: number; order: number }[];
    }>();

    for (const r of this.rows) {
      let amt = Number(r.opening_balance) || 0;
      // Paise-level leftovers from voucher rounding (e.g. GST खरेदी CGST 9%
      // at -0.01) aren't real balances - drop them before they reach any
      // total, so the ledger line doesn't show 0.02 built from them.
      if (Math.abs(amt) < MIN_BALANCE) { continue; }
      // Banks use the balance's own sign (DR negative, CR positive) like
      // every other ledger. master_opening_type is only the side of the
      // opening entry, not of the balance now - e.g. OD - 1706 opened Dr
      // but is 661,150.32 Cr after payments.
      const isBank = r.exp_id === 28;
      // Banks sit under Assets (the PHP marks CC/OD ones LIABILITIES), and
      // each bank is its own main line rather than a sub of बँक खाते.
      const section: Section = isBank ? 'ASSETS' : toSection(r.exp_type);
      const key = isBank ? `28|${r.sub_exp_id}` : `${r.exp_id}|${section}`;
      if (!map.has(key)) {
        map.set(key, {
          exp_name: isBank ? r.sub_exp_name : r.exp_name,
          exp_type: r.exp_type, section, total: 0, isBank,
          // Each bank is its own line, so banks order by their sub order
          // within बँक खाते's slot.
          expOrder: orderOf(r.exp_sort_order),
          subOrder: isBank ? orderOf(r.sub_sort_order) : 0,
          subs: []
        });
      }
      const g = map.get(key)!;
      g.total += amt;
      // Rows added here for a ledger the API also returned carry no
      // sort_order of their own - take it from the API row when it has one.
      if (r.exp_sort_order !== null && r.exp_sort_order !== undefined) {
        g.expOrder = Math.min(g.expOrder, r.exp_sort_order);
      }
      if (r.sub_exp_id && !isBank) {
        g.subs.push({ sub_exp_name: r.sub_exp_name, amount: amt, order: orderOf(r.sub_sort_order) });
      }
    }

    // Banks first (by their Sub_Expense_Master.sort_order), then ledgers by
    // Expense_master.sort_order; sub rows inside a ledger are sorted by
    // Sub_Expense_Master.sort_order below. Array.sort is stable, so rows
    // with equal / missing order keep the API's order.
    // Ledgers (and sub-ledgers) whose opening nets to 0.00 carry nothing into
    // the year, so they're dropped rather than shown as a 0.00 line.
    const isZero = (n: number) => Math.abs(n) < MIN_BALANCE;
    const ordered = Array.from(map.values())
      .sort((a, b) => (Number(b.isBank) - Number(a.isBank))
        || (a.expOrder - b.expOrder) || (a.subOrder - b.subOrder));
    this.groupedData = ordered.filter(g => !isZero(g.total)).map(g => ({
      exp_name: g.exp_name,
      exp_type: g.exp_type,
      section: g.section,
      opening_bal: Math.abs(g.total),
      // Group total spans multiple bank sub-accounts that can each sit on a
      // different side, so there's no single master_opening_type for it -
      // sign of the combined total is the only meaningful answer here.
      opening_bal_type: (g.total < 0 ? 'DR' : 'CR') as 'DR' | 'CR',
      // Banks have no sub rows any more, so the sign is the side everywhere.
      sub_expenses: g.subs.filter(s => !isZero(s.amount))
        .sort((a, b) => a.order - b.order)
        .map(s => ({
        sub_exp_name: s.sub_exp_name,
        opening_bal: Math.abs(s.amount),
        opening_bal_type: (s.amount < 0 ? 'DR' : 'CR') as 'DR' | 'CR'
      }))
    }));
  }

  getFormattedDate(): string {
    return this.datePipe.transform(this.currentDate, 'dd-MM-yyyy') || '';
  }

  // Display-only filter over already-grouped data - matches a group if its
  // own name or any of its sub-expense names contain the search text.
  get filteredGroups(): ExpenseGroup[] {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!term) { return this.groupedData; }
    return this.groupedData.filter(g =>
      g.exp_name.toLowerCase().includes(term) ||
      g.sub_expenses.some(s => s.sub_exp_name.toLowerCase().includes(term))
    );
  }

  // Assets first, then Liabilities, each with its own DR/CR subtotal. Empty
  // sections (e.g. nothing matches the search) are left out.
  get sections(): SectionView[] {
    return this.toSections(this.filteredGroups);
  }

  private toSections(groups: ExpenseGroup[]): SectionView[] {
    return (['ASSETS', 'LIABILITIES'] as Section[])
      .map(key => {
        const list = groups.filter(g => g.section === key);
        return {
          key,
          label: SECTION_LABELS[key],
          groups: list,
          totalDR: list.filter(g => g.opening_bal_type === 'DR').reduce((s, g) => s + g.opening_bal, 0),
          totalCR: list.filter(g => g.opening_bal_type === 'CR').reduce((s, g) => s + g.opening_bal, 0)
        };
      })
      .filter(s => s.groups.length > 0);
  }

  get grandTotalDR(): number {
    return this.groupedData
      .filter(g => g.opening_bal_type === 'DR')
      .reduce((s, g) => s + g.opening_bal, 0);
  }

  get grandTotalCR(): number {
    return this.groupedData
      .filter(g => g.opening_bal_type === 'CR')
      .reduce((s, g) => s + g.opening_bal, 0);
  }

  // Sanity indicator - should read 0 for a balanced opening sheet.
  get grandDifference(): number {
    return this.grandTotalDR - this.grandTotalCR;
  }

  exportExcel(fileName: string): void {
    const fmt = (d: any) => {
      if (!d) return '';
      try {
        const dt = new Date(d);
        const p = dt.toLocaleDateString('en-GB').split('/');
        return `${p[0]}-${p[1]}-${p[2]}`;
      } catch { return d?.toString() ?? ''; }
    };

    const rows: any[] = [];
    rows.push({ 'क्र.': 'हिंदुस्थान प्रकाशन संस्था', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'क्र.': 'विवेक साप्ताहिक', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'क्र.': 'Opening Balance Report', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'क्र.': `दिनांक पासून: ${fmt(this.startDate)}  पर्यंत: ${fmt(this.endDate)}`, 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'क्र.': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'क्र.': 'क्र.', 'तपशील': 'तपशील', 'डेबिट': 'डेबिट', 'क्रेडिट': 'क्रेडिट' });

    this.toSections(this.groupedData).forEach(section => {
      rows.push({ 'क्र.': '', 'तपशील': section.label, 'डेबिट': '', 'क्रेडिट': '' });
      let sr = 1;
      section.groups.forEach(g => {
        rows.push({
          'क्र.': sr++,
          'तपशील': g.exp_name,
          'डेबिट': g.opening_bal_type === 'DR' ? Number(g.opening_bal.toFixed(2)) : '',
          'क्रेडिट': g.opening_bal_type === 'CR' ? Number(g.opening_bal.toFixed(2)) : ''
        });
        // Sub rows only in "In Details" - Briefly exports main lines only,
        // matching what's on screen.
        if (!this.showDetails) { return; }
        g.sub_expenses.forEach(sub => {
          rows.push({
            'क्र.': '',
            'तपशील': '   -> ' + sub.sub_exp_name,
            'डेबिट': sub.opening_bal_type === 'DR' ? Number(sub.opening_bal.toFixed(2)) : '',
            'क्रेडिट': sub.opening_bal_type === 'CR' ? Number(sub.opening_bal.toFixed(2)) : ''
          });
        });
      });
      rows.push({
        'क्र.': '',
        'तपशील': `${section.label} एकूण :-`,
        'डेबिट': Number(section.totalDR.toFixed(2)),
        'क्रेडिट': Number(section.totalCR.toFixed(2))
      });
      rows.push({ 'क्र.': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });
    });

    rows.push({ 'क्र.': '', 'तपशील': 'एकूण :-', 'डेबिट': Number(this.grandTotalDR.toFixed(2)), 'क्रेडिट': Number(this.grandTotalCR.toFixed(2)) });

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows, { skipHeader: true });
    ws['!cols'] = [{ wch: 6 }, { wch: 45 }, { wch: 18 }, { wch: 18 }];
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Opening Balance');
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }
}
